import type { ModulePayload } from '@/login/types/AccessTokenData';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { CustomerInformationsEntity, SystemSettingsEntity } from '@sysarb/sysarb-types/general/types/legacyDatabase';
import type { BetaFlag, FeatureFlags, PasswordSettings, SetFeatureValue } from '@sysarb/sysarb-types/services/v1/authentication';
import { storeToRefs } from 'pinia';
import { computed, type ComputedRef } from 'vue';
import { useResgate } from '@/login/composables/useResgate';
import { instanceSettingsInputValidator, type InstanceSettings, type InstanceSettingsInput } from '@sysarb/sysarb-types/services/v1/instance/instanceSettings';
import { EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums/employmentSalaryType';
import type { CustomerSettings, UpdateCustomerSettingsInput } from '@sysarb/sysarb-types/services/v1/job';

type SettingReturn = {
    /**
     * Instance settings.
     */
    instanceSettings: ComputedRef<SystemSettingsEntity | null>;
    /**
     * Customer information.
     */
    customerInformation: ComputedRef<CustomerInformationsEntity | null>;
    /**
     * Security settings, like password requirements.
     */
    securitySettings: ComputedRef<PasswordSettings | null>;
    /**
     * Feature flags.
     */
    features: ComputedRef<FeatureFlags | null>;
    /**
     * Modules.
     */
    modules: ComputedRef<ModulePayload | null>;
    /**
     * Beta flags.
     */
    flags: ComputedRef<BetaFlag[]>;
    /**
     * System default salary type.
     */
    systemDefaultSalaryType: ComputedRef<EmploymentSalaryType | null>;
    /**
     * Job architecture settings.
     */
    jobArchitectureSettings: ComputedRef<CustomerSettings | null>;
    setJobArchitectureSettings: (input: UpdateCustomerSettingsInput) => Promise<boolean>;
    /**
     * Get & set involve settings.
     */
    involveSettings: ComputedRef<InstanceSettings | null>;
    setInvolveSettings: (payload: InstanceSettingsInput) => Promise<void>;

    setInstanceSetting: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setSecuritySetting: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setCustomerInformation: (name: string, value: AllowedSettingsValues) => Promise<boolean>;
    setFeature: (name: string, value: boolean) => Promise<boolean>;
    /**
     * Update default language on the current instance.
     */
    setInstanceLanguage: (language: string) => Promise<void>;
};

export type AllowedSettingsValues = string | Date | number | boolean | null | undefined;

export function useSettings(): SettingReturn {
    const loginStore = useLoginStore();
    const { selectedInstance, userData, userId, instanceTopic } = storeToRefs(loginStore);

    const setJobArchitectureSettings = async (input: UpdateCustomerSettingsInput): Promise<boolean> => {
        const resgate = useResgate(crypto.randomUUID());
        const result = await resgate.call(`v1.${instanceTopic.value}.job-architecture-settings`, 'set', input).then(r => {
            resgate.unsubscribe();
            return r;
        });
        return result.success;
    };

    const instanceSettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.settings;
    });

    const customerInformation = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.customer;
    });

    const securitySettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.security;
    });

    const features = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.features;
    });

    const modules = computed(() => {
        if (userData.value == null) return null;
        return userData.value.modules;
    });

    const flags = computed(() => {
        if (selectedInstance.value?.flags?.flags == null) return [];
        return selectedInstance.value.flags.flags;
    });

    const setFeature = async (name: string, value: boolean): Promise<boolean> => {
        if (features.value == null) return false;

        const data: SetFeatureValue = {
            name: name as keyof FeatureFlags,
            enabled: value
        };

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>('v1.client.features', 'set', data).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const involveSettings = computed<InstanceSettings | null>(() => {
        if (selectedInstance.value == null) return null;

        const settings = selectedInstance.value.involveSettings;
        return settings;
    });

    const setInstanceLanguage = async (language: string): Promise<void> => {
        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}`, 'setlanguage', {
            language
        }).then(() => {
            resgate.unsubscribe();
        });
    };

    const setInvolveSettings = async (payload: InstanceSettingsInput): Promise<void> => {
        const resgate = useResgate(crypto.randomUUID());
        const validatedPayload = instanceSettingsInputValidator.safeParse(payload);

        if (validatedPayload.success === true) {
            await resgate.call<InstanceSettings>(`v1.${instanceTopic.value}.system.instance.display-settings`, 'set', validatedPayload.data);
        } else {
            console.error('Invalid payload', validatedPayload.error);
        }
    };

    const setInstanceSetting = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (instanceSettings.value == null) return false;
        if (!(name in instanceSettings.value)) {
            console.warn('Trying to change non-existing instance setting', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.settings`, 'set', {
            [name]: value
        }).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const setSecuritySetting = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (securitySettings.value == null) return false;
        if (!(name in securitySettings.value)) {
            console.warn('Trying to change non-existing security setting', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.security`, 'set', {
            [name]: value
        }).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const setCustomerInformation = async (name: string, value: AllowedSettingsValues): Promise<boolean> => {
        if (customerInformation.value == null) return false;
        if (!(name in customerInformation.value)) {
            console.warn('Trying to change non-existing customer information', name);
            return false;
        }

        const resgate = useResgate(crypto.randomUUID());
        await resgate.call<void>(`v2.access.token.${userId.value}.instance.${instanceTopic.value}.customer`, 'set', {
            [name]: value
        }).then(() => {
            resgate.unsubscribe();
        });

        return true;
    };

    const systemDefaultSalaryType = computed<EmploymentSalaryType | null>(() => {
        if (instanceSettings.value?.DefaultSalaryType == null) return null;

        switch (parseInt(instanceSettings.value.DefaultSalaryType)) {
            case EmploymentSalaryType.hourly:
                return EmploymentSalaryType.hourly;
            case EmploymentSalaryType.monthly:
                return EmploymentSalaryType.monthly;
            case EmploymentSalaryType.yearly:
                return EmploymentSalaryType.yearly;
            default:
                return null;
        }
    });

    const jobArchitectureSettings = computed(() => {
        if (selectedInstance.value == null) return null;
        return selectedInstance.value.jobArchitectureSettings;
    });

    return {
        instanceSettings,
        customerInformation,
        securitySettings,
        features,
        modules,
        flags,
        involveSettings,
        setInvolveSettings,
        setInstanceSetting,
        setSecuritySetting,
        setCustomerInformation,
        setFeature,
        setInstanceLanguage,
        systemDefaultSalaryType,
        jobArchitectureSettings,
        setJobArchitectureSettings,
    };
}
